import type { ReactNode } from 'react';
import styles from './ScheduleErrorModal.module.css';

/** Ein-Klick-Retry-Angebot (z.B. "mit mehr Wiggle Room erneut rechnen"). */
export interface SolverRetryOption {
  key: string;
  title: string;
  description?: ReactNode;
  [key: string]: unknown;
}

interface SolverErrorStudent {
  name?: string | null;
  [key: string]: unknown;
}

export interface SolverErrorDetails {
  message?: string | null;
  error_type?: string | null;
  skipped_students?: Array<string | SolverErrorStudent> | null;
  unscheduled_students?: Array<string | SolverErrorStudent> | null;
  [key: string]: unknown;
}

interface ScheduleErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorDetails?: SolverErrorDetails | null;
  onNavigateToStep2?: (() => void) | null;
  retryOptions?: SolverRetryOption[];
  onRetry?: (option: SolverRetryOption) => void;
}

export default function ScheduleErrorModal({ isOpen, onClose, errorDetails, onNavigateToStep2, retryOptions = [], onRetry }: ScheduleErrorModalProps) {
  if (!isOpen || !errorDetails) return null;

  const { message, skipped_students, unscheduled_students } = errorDetails;

  const translateKnownSolverText = (rawText: string | null | undefined): string => {
    if (!rawText || typeof rawText !== 'string') return '';

    const replacements = [
      {
        pattern: /Could not find a schedule that satisfies all constraints \(infeasible\)\.?/gi,
        replacement: 'Es konnte kein Stundenplan gefunden werden, der alle aktuellen Bedingungen erfüllt (infeasible).'
      },
      {
        pattern: /Initial filtering skipped students due to unavailability:\s*/gi,
        replacement: 'In der Vorprüfung wegen fehlender Verfügbarkeit übersprungen: '
      },
      {
        pattern: /Could not schedule at least\s+(\d+)\s+out of\s+(\d+)\s+relevant students\.?/gi,
        replacement: 'Mindestanforderung nicht erreicht: Mindestens $1 von $2 relevanten Schüler:innen müssen eingeplant werden.'
      },
      {
        pattern: /Students skipped due to unavailability during preprocessing:\s*/gi,
        replacement: 'In der Vorprüfung wegen fehlender Verfügbarkeit übersprungen: '
      },
      {
        pattern: /Students left unscheduled in final plan:\s*/gi,
        replacement: 'Im finalen Plan nicht eingeplant: '
      },
      {
        pattern: /The scheduling model is invalid\. Check constraints and variable definitions\.?/gi,
        replacement: 'Das Berechnungsmodell ist ungültig. Bitte die Nebenbedingungen und Variablendefinitionen prüfen.'
      },
      {
        pattern: /Solver finished with status code\s+(\d+)\. No optimal or feasible solution guaranteed\.?/gi,
        replacement: 'Die Berechnung wurde mit Statuscode $1 beendet. Eine optimale oder zulässige Lösung konnte nicht garantiert werden.'
      }
    ];

    let translated = rawText;
    replacements.forEach(({ pattern, replacement }) => {
      translated = translated.replace(pattern, replacement);
    });
    return translated;
  };

  const getFormattedMessageLines = (rawMessage: string | null | undefined): string[] => {
    if (!rawMessage) return [];

    const withoutPrefix = rawMessage
      .replace(/^Stundenplan konnte nicht erstellt werden:\s*/i, '')
      .trim();

    const translated = translateKnownSolverText(withoutPrefix);
    const sentenceSplit = translated
      .replace(/\. (?=[A-ZA-Z])/g, '.\n')
      .replace(/\s*\n\s*/g, '\n')
      .trim();

    return sentenceSplit
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  };

  const renderStudentList = (students: Array<string | SolverErrorStudent> | null | undefined, title: string) => {
    if (!students || students.length === 0) return null;

    return (
      <div className={styles.studentList}>
        <h4>{title}</h4>
        <ul>
          {students.map((student, index) => (
            <li key={index}>
              {typeof student === 'string' ? student : student.name || (student as any)}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const messageLines = getFormattedMessageLines(message);
  const canRetry = retryOptions.length > 0 && typeof onRetry === 'function';

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>×</button>

        <div className={styles.header}>
          <div className={styles.icon}>ℹ️</div>
          <h2>Stundenplan konnte nicht erstellt werden</h2>
        </div>

        <div className={styles.content}>
          <p className={styles.mainMessage}>
            {errorDetails.error_type === "INFEASIBLE"
              ? "Es gibt keine Lösung für die aktuellen Einstellungen. Bitte ändern Sie die Parameter, um einen Stundenplan zu erstellen."
              : errorDetails.error_type === "TIMEOUT"
                ? "Die Berechnung dauert ungewöhnlich lange und hat das Zeitlimit erreicht."
                : errorDetails.error_type === "NETWORK"
                  ? "Die Verbindung zum Server wurde unterbrochen. Bitte versuchen Sie es erneut."
                  : errorDetails.error_type === "UNAUTHORIZED"
                    ? "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an."
              : "Die automatische Stundenplanerstellung war nicht erfolgreich. Dies kann verschiedene Gründe haben."
            }
          </p>

          {/* Details und Namenslisten nebeneinander — spart Höhe, nutzt die Breite */}
          <div className={styles.infoGrid}>
            {messageLines.length > 0 && (
              <div className={styles.errorDetails}>
                <h4 className={styles.errorDetailsTitle}>Details</h4>
                {messageLines.length === 1 ? (
                  <p className={styles.errorMessage}>{messageLines[0]}</p>
                ) : (
                  <ul className={styles.errorMessageList}>
                    {messageLines.map((line, index) => (
                      <li key={`${line}-${index}`}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {renderStudentList(skipped_students, "Ohne Zeitverfügbarkeit")}
            {renderStudentList(unscheduled_students, "Nicht eingeplant")}
          </div>

          {/* Konkrete Angebote statt reiner Ratschläge: ein Klick startet die
              Berechnung direkt mit der geänderten Einstellung neu. */}
          {canRetry ? (
            <div className={styles.suggestions}>
              <h3>Soll ich es noch einmal versuchen?</h3>
              <div className={styles.retryList}>
                {retryOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={styles.retryOption}
                    onClick={() => onRetry?.(option)}
                  >
                    <span className={styles.retryTitle}>{option.title}</span>
                    <span className={styles.retryDescription}>{option.description}</span>
                  </button>
                ))}
              </div>
              <p className={styles.retryFootnote}>
                Alternativ die Zeiten anpassen – deine eigenen in Schritt 1 oder die der Schüler:innen.
              </p>
            </div>
          ) : (
            <div className={styles.suggestions}>
              <h3>Lösungsvorschläge</h3>
              <ul>
                <li>Erhöhe <strong>„Unverplant max.“</strong> in der Werkzeugleiste – so viele Personen dürfen unverplant bleiben</li>
                <li>Passe deine <strong>Zeiten in Schritt 1 „Deine Zeiten“</strong> an</li>
                <li>Überprüfen Sie die Zeitverfügbarkeiten der Studenten</li>
              </ul>
            </div>
          )}
        </div>

        <div className={styles.actions}>
          {onNavigateToStep2 && (
            <button
              className={styles.secondaryButton}
              onClick={onNavigateToStep2}
            >
              Zeitverfügbarkeiten anpassen
            </button>
          )}
          <button
            className={styles.primaryButton}
            onClick={onClose}
          >
            {canRetry ? 'Nein, schließen' : 'Schließen'}
          </button>
        </div>
      </div>
    </div>
  );
}
